import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { fileViewUrl, formatErr } from "../lib/api";
import PhotoUpload from "../components/PhotoUpload";
import { toast } from "sonner";
import { Camera, Mail, Phone, GraduationCap, UserRound, IdCard, Shield, Save, BookOpen, Hash, Briefcase } from "lucide-react";
import { COURSES, BATCHES, DESIGNATIONS, withCurrent } from "../lib/constants";

export default function Account() {
  const { user, setUser } = useAuth();
  const [photoPath, setPhotoPath] = useState(user?.photo_path || "");
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [registrationNumber, setRegistrationNumber] = useState(user?.registration_number || "");
  const [rollNumber, setRollNumber] = useState(user?.roll_number || "");
  const [degree, setDegree] = useState(user?.degree || "");
  const [batch, setBatch] = useState(user?.batch || "");
  const [designation, setDesignation] = useState(user?.designation || "");
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const initials = (user.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  const roleLabel = user.role === "manager" ? "Manager" : user.role === "faculty" ? "Faculty" : "Student";
  const roleColor =
    user.role === "manager" ? "#2B5F4E"
    : user.role === "faculty" ? "#367661"
    : "#D78A76";

  const isStudent = user.role === "student";
  const isFaculty = user.role === "faculty" || user.role === "manager";

  const photoArr = photoPath ? [photoPath] : [];
  const onPhotoChange = (arr) => setPhotoPath(arr[0] || "");

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        photo_path: photoPath || null,
        phone,
        name,
      };
      if (isStudent) {
        payload.registration_number = registrationNumber;
        payload.roll_number = rollNumber;
        payload.degree = degree;
        payload.batch = batch;
      }
      if (isFaculty) {
        payload.designation = designation;
      }
      const r = await api.put("/me", payload);
      setUser(r.data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    (photoPath || "") !== (user.photo_path || "") ||
    name !== (user.name || "") ||
    phone !== (user.phone || "") ||
    (isStudent && (
      registrationNumber !== (user.registration_number || "") ||
      rollNumber !== (user.roll_number || "") ||
      degree !== (user.degree || "") ||
      batch !== (user.batch || "")
    )) ||
    (isFaculty && designation !== (user.designation || ""));

  return (
    <div className="max-w-4xl mx-auto space-y-5 animate-fade-up" data-testid="account-page">
      {/* Hero */}
      <div className="rounded-2xl bg-white border border-brand-border shadow-tactile overflow-hidden">
        <div className="dashboard-hero p-6 sm:p-8 text-white relative">
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="relative">
              {photoPath ? (
                <img src={fileViewUrl(photoPath)} alt="" className="h-24 w-24 rounded-full object-cover border-4 border-white/80 shadow-lg" />
              ) : (
                <div className="h-24 w-24 rounded-full border-4 border-white/80 shadow-lg grid place-items-center font-heading text-3xl font-semibold" style={{ background: "#ffffff20", color: "#fff" }}>
                  {initials || "?"}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold text-white shadow" style={{ background: roleColor }}>
                <Shield className="h-3 w-3" /> {roleLabel}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">Account & Profile</div>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 tracking-tight">{user.name}</h1>
              <div className="text-white/85 text-sm mt-1 truncate">{user.email}</div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {isFaculty && user.designation && <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" /> {user.designation}</span>}
                {isStudent && user.degree && <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> {user.degree}</span>}
                {isStudent && user.batch  && <span className="inline-flex items-center gap-1.5"><BookOpen      className="h-3.5 w-3.5" /> Batch {user.batch}</span>}
                {isStudent && user.registration_number && <span className="inline-flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" /> Reg# {user.registration_number}</span>}
                {isStudent && user.roll_number && <span className="inline-flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5" /> Roll {user.roll_number}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo upload */}
      <div className="rounded-xl bg-white border border-brand-border shadow-tactile p-5" data-testid="profile-photo-section">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="h-9 w-9 rounded-lg bg-brand-primary/10 grid place-items-center">
            <Camera className="h-4 w-4 text-brand-primary" />
          </div>
          <div>
            <div className="font-heading font-semibold text-base">Profile Photo</div>
            <div className="text-xs text-brand-muted">A clear face photo helps your team recognise you across visit records.</div>
          </div>
        </div>
        <PhotoUpload value={photoArr} onChange={onPhotoChange} multiple={false} maxCount={1} testId="account-photo" />
        {photoPath && (
          <button
            type="button"
            onClick={() => setPhotoPath("")}
            className="mt-3 text-xs text-brand-danger font-semibold hover:underline"
            data-testid="remove-photo-btn"
          >
            Remove photo
          </button>
        )}
      </div>

      {/* Editable fields */}
      <div className="rounded-xl bg-white border border-brand-border shadow-tactile p-5" data-testid="profile-info-section">
        <div className="font-heading font-semibold text-base mb-4">Profile Info</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name" icon={UserRound}>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name" />
          </Field>
          <Field label="Phone" icon={Phone}>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Contact number" data-testid="account-phone" />
          </Field>
          <Field label="Email" icon={Mail} readOnly>
            <Input value={user.email} readOnly disabled />
          </Field>
          <Field label="Role" icon={Shield} readOnly>
            <Input value={roleLabel} readOnly disabled />
          </Field>

          {/* === Student-specific === */}
          {isStudent && (
            <>
              <Field label="Registration Number" icon={Hash}>
                <Input value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} placeholder="ACN-2024-001" data-testid="account-registration" />
              </Field>
              <Field label="Roll Number" icon={IdCard}>
                <Input value={rollNumber} onChange={(e) => setRollNumber(e.target.value)} data-testid="account-roll" />
              </Field>
              <Field label="Course" icon={GraduationCap}>
                <Select value={degree} onChange={(e) => setDegree(e.target.value)} data-testid="account-course">
                  <option value="">Select course…</option>
                  {withCurrent(COURSES, degree).map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Batch (Joining Year)" icon={BookOpen}>
                <Select value={batch} onChange={(e) => setBatch(e.target.value)} data-testid="account-batch">
                  <option value="">Select joining year…</option>
                  {withCurrent(BATCHES, batch).map((b) => <option key={b} value={b}>{b}</option>)}
                </Select>
              </Field>
            </>
          )}

          {/* === Faculty/Manager-specific === */}
          {isFaculty && (
            <Field label="Designation" icon={Briefcase}>
              <Select value={designation} onChange={(e) => setDesignation(e.target.value)} data-testid="account-designation">
                <option value="">Select designation…</option>
                {withCurrent(DESIGNATIONS, designation).map((d) => <option key={d} value={d}>{d}</option>)}
              </Select>
            </Field>
          )}
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="save-profile-btn"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children, readOnly }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted inline-flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}{readOnly && <span className="text-brand-muted/70">· read-only</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Input(props) {
  return <input {...props} className="w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none disabled:opacity-60 disabled:cursor-not-allowed" />;
}

function Select({ children, ...props }) {
  return <select {...props} className="w-full h-11 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none">{children}</select>;
}
